/**
 * KAN-758: высота блока "до низа экрана".
 *
 * Считает max-height элемента как innerHeight - верх элемента - нижний отступ,
 * без магических чисел про высоту шапки страницы. Нижний отступ не меньше
 * bottomOffset и не меньше суммы нижних padding/border предков до
 * прокручиваемого контейнера (рамка карточки, отступы страницы): так контейнер
 * страницы не прокручивается, а горизонтальная полоса всегда видна внизу экрана.
 *
 * Пересчёт: resize окна и изменение размеров содержимого прокручиваемого
 * контейнера (шапка, панель периода, заголовок карточки) через ResizeObserver.
 * На низком экране (innerHeight < smallScreenHeight) ограничения нет:
 * обычная прокрутка страницы.
 *
 * KAN-766: предок с position: fixed (блок, развёрнутый на весь экран) считается
 * контейнером: выше него разметка страницы на высоту не влияет. Смена опций
 * (режим развёрнут/свёрнут передаёт свои bottomOffset/smallScreenHeight)
 * пересчитывает высоту и переподписывает наблюдателей на новый контейнер.
 *
 * KAN-865: mode 'height' задаёт высоту (height), а не max-height: блок занимает
 * экран до низа и при малом содержимом (канбан-доска, колонки на всю высоту).
 */
import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

export const FILL_BOTTOM_OFFSET = 16;
export const FILL_MIN_HEIGHT = 300;
export const FILL_SMALL_SCREEN_HEIGHT = 500;

interface FillViewportOptions {
  bottomOffset?: number;
  minHeight?: number;
  smallScreenHeight?: number;
}

interface FillViewportHookOptions extends FillViewportOptions {
  /** 'max' (по умолчанию): max-height; 'height': фиксированная высота. */
  mode?: 'max' | 'height';
}

/** Ближайший предок с вертикальной прокруткой или position: fixed, иначе корень документа. */
export function findScrollParent(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY, position } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || position === 'fixed') return node;
    node = node.parentElement;
  }
  return document.documentElement;
}

/** Вертикальная прокрутка контейнера (корень документа: window.scrollY). */
function scrollTopOf(parent: HTMLElement): number {
  return parent === document.documentElement ? window.scrollY : parent.scrollTop;
}

/**
 * Нижние padding + border предков от родителя элемента до прокручиваемого
 * контейнера включительно: столько места ниже обёртки занимает разметка.
 * Не зависит от высоты таблицы, поэтому обёртка растёт при новых строках.
 */
function spaceBelow(el: HTMLElement, parent: HTMLElement): number {
  let total = 0;
  let node = el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    total += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    if (node === parent) break;
    node = node.parentElement;
  }
  return total;
}

export function computeFillHeight(
  el: HTMLElement,
  { bottomOffset = FILL_BOTTOM_OFFSET, minHeight = FILL_MIN_HEIGHT, smallScreenHeight = FILL_SMALL_SCREEN_HEIGHT }: FillViewportOptions = {},
): number | null {
  const viewport = window.innerHeight;
  if (viewport < smallScreenHeight) return null;
  const parent = findScrollParent(el);
  const gap = Math.max(bottomOffset, Math.ceil(spaceBelow(el, parent)));
  // Верх в непрокрученном положении контейнера: прокрутка страницы не меняет высоту.
  const top = el.getBoundingClientRect().top + scrollTopOf(parent);
  return Math.max(minHeight, Math.floor(viewport - top - gap));
}

export function useFillViewportHeight<T extends HTMLElement = HTMLDivElement>(options: FillViewportHookOptions = {}) {
  const ref = useRef<T | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const { bottomOffset, minHeight, smallScreenHeight } = options;

  const recalc = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setHeight(computeFillHeight(el, { bottomOffset, minHeight, smallScreenHeight }));
  }, [bottomOffset, minHeight, smallScreenHeight]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    recalc();
    window.addEventListener('resize', recalc);
    let observer: ResizeObserver | undefined;
    let frame: number | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          frame = null;
          recalc();
        });
      });
      const parent = findScrollParent(el);
      observer.observe(document.body);
      if (parent !== document.documentElement) observer.observe(parent);
      for (const child of Array.from(parent.children)) observer.observe(child);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }
    return () => {
      window.removeEventListener('resize', recalc);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [recalc]);

  let style: CSSProperties = {};
  if (height !== null) style = options.mode === 'height' ? { height: `${height}px` } : { maxHeight: `${height}px` };
  return { ref, style, height };
}
